import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import EuroIcon from '@mui/icons-material/EuroSymbol';
import { useTranslation } from 'react-i18next';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { formatDurationSeconds } from './timeEntries';
import { colors } from '../../core/theme/theme';

export interface TimeEntryDetailDrawerProps {
  entry: TimeEntry | null;
  open: boolean;
  onClose: () => void;
  projectLabel?: string;
  designationLabel?: string;
  userLabel?: string;
  /** Edit/Delete buttons render only when these handlers are supplied. */
  onEdit?: () => void;
  onDelete?: () => void;
}

/**
 * Read-first drawer that surfaces every field of a TimeEntry in one
 * place. Opened by clicking a row. Edit / delete actions are offered
 * only when the parent passes `editable` (i.e. the tracker page;
 * the per-project tab is read-only and omits them).
 *
 * Lateral (right-anchored) because it lets the reader skim the list
 * while inspecting one row without losing scroll position.
 */
export default function TimeEntryDetailDrawer({
  entry,
  open,
  onClose,
  projectLabel,
  designationLabel,
  userLabel,
  onEdit,
  onDelete,
}: TimeEntryDetailDrawerProps) {
  const { t, i18n } = useTranslation();

  // All derived values guard on `entry` being null — the drawer can
  // render during the close animation after the parent nulls it.
  const startDate = entry?.startTime.toDate();
  const endDate = entry?.endTime.toDate();
  const dayLabel = startDate
    ? new Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(startDate)
    : '';
  const timeRange = startDate && endDate
    ? `${new Intl.DateTimeFormat(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(startDate)} – ${new Intl.DateTimeFormat(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(endDate)}`
    : '';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: '100%', sm: 420 }, borderRadius: 0 } },
      }}
      data-testid="time-entry-detail-drawer"
    >
      {/* Header ─────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${colors.lightGray}`,
        }}
      >
        <Typography variant="h4">{t('kronos.entries.detailTitle')}</Typography>
        <IconButton onClick={onClose} aria-label={t('common.cancel')} data-testid="time-entry-detail-close">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body ───────────────────────────────────────── */}
      {entry && (
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          <Stack spacing={2}>
            <Field label={t('kronos.entries.whenColumn')} value={dayLabel} />
            <Field
              label={t('kronos.entries.hoursRange')}
              value={timeRange}
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {formatDurationSeconds(entry.durationSeconds)}
                </Typography>
              }
            />
            {projectLabel && (
              <Field label={t('kronos.entries.projectColumn')} value={projectLabel} />
            )}
            {designationLabel && (
              <Field label={t('kronos.entries.designationColumn')} value={designationLabel} />
            )}
            {userLabel && (
              <Field label={t('kronos.entries.teammate')} value={userLabel} />
            )}
            <Field
              label={t('kronos.entries.descriptionColumn')}
              value={
                entry.description && entry.description.trim() !== ''
                  ? entry.description
                  : '—'
              }
              allowWrap
            />
            {entry.tags.length > 0 && (
              <Box>
                <FieldLabel label={t('kronos.tracker.tags')} />
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {entry.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
            <Box>
              <FieldLabel label={t('kronos.tracker.billable')} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                {entry.billable ? (
                  <>
                    <EuroIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body2">
                      {t('kronos.entries.billableYes')}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('kronos.entries.billableNo')}
                  </Typography>
                )}
              </Box>
            </Box>
            <Divider />
            <Field
              label={t('kronos.entries.source')}
              value={t(`kronos.entries.source_${entry.source}`)}
            />
            {entry.timesheetId && (
              <Box>
                <FieldLabel label={t('kronos.entries.timesheetLink')} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {entry.timesheetId}
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {/* Footer actions ─────────────────────────────── */}
      {entry && (onEdit || onDelete) && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${colors.lightGray}`,
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          {onDelete && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DeleteIcon />}
              onClick={onDelete}
              data-testid="time-entry-detail-delete"
            >
              {t('common.delete')}
            </Button>
          )}
          {onEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={onEdit}
              data-testid="time-entry-detail-edit"
            >
              {t('common.edit')}
            </Button>
          )}
        </Box>
      )}
    </Drawer>
  );
}

// ── Private helpers ─────────────────────────────────────────────────

function FieldLabel({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      sx={{
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: 'text.secondary',
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
  );
}

function Field({
  label,
  value,
  secondary,
  allowWrap,
}: {
  label: string;
  value: string;
  secondary?: React.ReactNode;
  allowWrap?: boolean;
}) {
  return (
    <Box>
      <FieldLabel label={label} />
      <Typography
        variant="body2"
        sx={{
          mt: 0.25,
          whiteSpace: allowWrap ? 'pre-wrap' : undefined,
          wordBreak: allowWrap ? 'break-word' : undefined,
        }}
      >
        {value}
      </Typography>
      {secondary}
    </Box>
  );
}
